import { useCallback, useEffect, useRef } from "react";
import type { TextCanvasObject } from "@refinement-canvas/domain";
import { usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useObjectTextEditor } from "../input/use-object-text-editor";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { MarkdownTextView } from "./markdown-text-view";

interface Props {
  readonly object: TextCanvasObject;
}

// テキストオブジェクト（直接書き込み可能）。
// - 単一クリックで選択、ダブルクリックで編集モードへ
// - 編集中は IME composition を考慮しつつ 50ms debounce で text.update op を送信
export function TextObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const autoEditObjectId = useCanvasStore((s) => s.autoEditObjectId);
  const setAutoEditObjectId = useCanvasStore((s) => s.setAutoEditObjectId);

  // 0 文字（空白のみ）で確定したテキスト図形は、キャンバス上で見えず位置も分からなくなるため自動削除する。
  const handleEmptyCommit = useCallback(() => {
    dispatcher.deleteObject(object.id);
  }, [dispatcher, object.id]);

  // 編集中は、図形枠の高さ（object.h）を本文の実描画高さへ自動で追随させる（伸びても縮んでも）。
  // 保存上の枠（object.h）が実体より小さいと選択枠・範囲選択・ミニマップ・他参加者側の表示が
  // 本文の下側を取りこぼし、逆に大きいままだと本文の無い余白が枠として残り続けるため、
  // 編集しているクライアントが h を実測へ合わせる（多クライアントの測定差で op を打ち合わない
  // よう、追随を行うのは編集中のクライアントだけに限定する）。
  // 実測はコンテナではなくエディタ要素で行う——コンテナは minHeight: object.h で下駄を履くため
  // 縮小方向の実測（本文が減った・枠が本文より大きい）を観測できない。エディタの offsetHeight は
  // レイアウト上の高さで、ステージの scale(zoom) や自身の rotate の影響を受けない。
  const fitHeightToContent = useCallback(
    (allowShrink: boolean) => {
      const editorEl = editorRef.current;
      if (!editorEl) return;
      const rendered = editorEl.offsetHeight;
      // 空の contentEditable は高さ 0 になり得るため、その場合は枠を変更しない。
      if (rendered <= 0) return;
      dispatcher.autoFitObjectHeight(object.id, rendered, { allowShrink });
    },
    [dispatcher, object.id]
  );

  // 入力中のライブ追随は拡張のみ。縮小まで許すと、同時編集で短いローカル本文を保持している
  // クライアントが他者の長い本文の高さを縮め戻してしまう（autoFitObjectHeight の interface
  // コメント参照）。縮小は下の「編集開始時・編集確定時」の再実測（権威本文と対応する実測）で行う。
  const handleLiveText = useCallback(() => {
    fitHeightToContent(false);
  }, [fitHeightToContent]);

  const editor = useObjectTextEditor({
    objectId: object.id,
    initialText: object.text,
    elementRef: editorRef,
    dispatcher,
    disabled: object.locked,
    onEmptyCommit: handleEmptyCommit,
    onLiveText: handleLiveText,
    // 非編集表示が LinkifiedText のため、リンク付き HTML の貼り付けを [テキスト](URL) で取り込める
    preserveHtmlAnchorsOnPaste: true,
    // 編集中も Markdown 記法を即時 WYSIWYG 整形する（マーカーは非表示。保存は生テキストのまま）。
    markdown: true,
  });

  // 編集開始時・編集確定時に枠の高さを本文へ合わせ直す（縮小もここでだけ許可する）。
  // - 編集開始時: エディタは権威本文（object.text）で初期化されるため、実測＝権威本文の高さ。
  //   過去に大きくなったままの枠も、ダブルクリックで編集に入るだけで実測へ収束する。
  // - 編集確定時: leaveEditMode の flush で自分の本文が権威（LWW）になった直後のため、
  //   非編集表示（LinkifiedText）の実測に合わせて縮められる。
  // useObjectTextEditor 内の編集開始 effect（contentEditable へ本文を反映・focus）より後に
  // 実行されるよう、フックより後で宣言した passive effect にする（先に測ると空高さを拾う）。
  const prevEditingRef = useRef(false);
  useEffect(() => {
    const wasEditing = prevEditingRef.current;
    prevEditingRef.current = editor.isEditing;
    if (editor.isEditing) {
      // 編集開始
      fitHeightToContent(true);
    } else if (wasEditing) {
      // 編集確定（0 文字時は onEmptyCommit で削除済みのことがある。autoFitObjectHeight 側の
      // 存在チェックで無害に no-op になる）
      fitHeightToContent(true);
    }
  }, [editor.isEditing, fitHeightToContent]);

  // 配置直後（place-text で空テキストを作成した直後）に自動で編集モードへ入り、すぐ入力できるようにする。
  // 配置クリック直後に focus すると、クリックの既定 focus 処理と競合して編集領域が即 blur し、
  // 「0 文字確定」とみなされて自動削除されてしまう（配置したテキストが一瞬で消える）。これを避けるため、
  // クリックが落ち着く次フレーム（requestAnimationFrame）で編集モードへ入る。
  // 依存には安定な enterEditMode / setter のみを置く（editor オブジェクトは毎レンダー同一性が変わるため
  // 含めない）。ref ガードは付けない——StrictMode の effect 二重起動（run→cleanup→run）で cleanup が
  // 初回 rAF を取り消すため、ガードで再スケジュールを抑止すると enterEditMode が一度も走らなくなる。
  // ガード無しなら「最後の run が張った rAF」が生き残り、rAF 内の setAutoEditObjectId(null) で以後の
  // 再実行は早期 return するため、多重実行にもならない。
  const enterEditMode = editor.enterEditMode;
  useEffect(() => {
    if (autoEditObjectId !== object.id) return;
    if (object.locked) {
      setAutoEditObjectId(null);
      return;
    }
    const raf = requestAnimationFrame(() => {
      enterEditMode();
      setAutoEditObjectId(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [autoEditObjectId, object.id, object.locked, enterEditMode, setAutoEditObjectId]);

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: editor.isEditing || activeTool === "draw-pen",
  });

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="text"
      onDoubleClick={editor.enterEditMode}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        minHeight: `${object.h}px`,
        zIndex: object.zOrder,
        fontSize: `${object.fontSize ?? 16}px`,
        color: object.color ?? "var(--color-text)",
        whiteSpace: "pre-wrap",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        // 回転支点は「実際に描画されている要素の中心」にする。minHeight 運用で複数行テキストの
        // 実描画高さが object.h を超えても、選択枠（実描画高さ boxHeight・transformOrigin:center）と
        // 回転ハンドル（centerY = object.y + boxHeight/2）と同じ支点で回転し、枠が本体から外れないようにする。
        transformOrigin: "center",
        // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする。
        outline: isSelected
          ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
          : "none",
        // 編集中は text カーソルを優先。非編集時、配置ツール・ペン描画の選択中は新規作成（crosshair）を示し移動と誤認させない。
        cursor: editor.isEditing ? "text" : usesCrosshairCursor(activeTool) ? "crosshair" : "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: editor.isEditing ? "text" : "none",
      }}
    >
      <div
        // 編集用（contentEditable + 生テキスト）と表示用（Markdown 整形）で DOM ノードの寿命を
        // 分離する。編集中はブラウザと useObjectTextEditor の effect が React 管理外のノード
        // （テキストノード・<div>/<br>）を作るため、同一ノードのまま整形ビュー（React 要素）
        // へ切り替えると reconciliation が残骸を除去できず二重表示になる。key で強制再マウント
        // すれば遷移毎に要素ごと作り直され、この問題が構造的に起きない。
        key={editor.isEditing ? "edit" : "view"}
        ref={editorRef}
        data-testid={`canvas-object-text-${object.id}`}
        style={{
          width: "100%",
          minHeight: "100%",
          outline: "none",
          cursor: "inherit",
          // 文字寄せ（左/中央/右）。未指定は左寄せ。編集中・確定後の両方に適用する。
          // 箇条書き行のみ MarkdownTextView 側で常に左寄せへ上書きする。
          textAlign: object.textAlign ?? "left",
          // 長い URL・英単語も枠（w）内で必ず折り返す。編集中（contentEditable）はブラウザ既定で
          // 強制折り返しされるのに、確定後の表示は折り返し指定が無く「?」等の区切りまで折り返さず
          // 枠の右へはみ出していた（入力中と確定後で枠と本文の対応が食い違う原因）。
          // 付箋のエディタと同じ指定で、編集中・確定後の折り返し挙動を一致させる。
          overflowWrap: "anywhere",
        }}
        {...editor.editorProps}
      >
        {/* Markdown 整形（行単位）の中で URL を LinkifiedText によりリンク化する（両機能を合成）。 */}
        {editor.isEditing ? null : <MarkdownTextView text={object.text} />}
      </div>
    </div>
  );
}
